import {
  configure,
  getCurrentUser,
  signInWithRedirect,
} from "data/services/amplify/AmplifyService";
import { AmplifyInitializationError } from "data/@types/AmplifyInitializationError";
import {
  setAxiosAuthorizationHeader,
  initializeAxiosInstances,
} from "data/services/api/axios-config";
import { useAuthStore } from "data/store/Auth";

export enum AppBootstrapStatus {
  AMPLIFY_INITIALIZATION_ERROR = "Error with AWS Amplify Configuration.",
  SUCCESS = "Application Loaded Successfully.",
  USER_AUTHENTICATION = "Autenticating User on AWS Cognito User Pool.",
  USER_AUTHENTICATION_ERROR = "User Authentication Error.",
}

const initUser = async (callback: () => void) => {
  const user = await getCurrentUser();
  const setUser = useAuthStore.getState().setUser;

  if (!user) {
    setTimeout(() => initUser(callback));
  } else {
    setAxiosAuthorizationHeader(`Bearer ${user.identityToken}`);
    setUser(user);
    callback();
  }
};

/**
 * Intercept the /token endpoint during the Amplify login.
 * If the response is successful, the Axios Authorization Header is set globally
 * and a success status is returned, enabling the application to be rendered
 */
const overrideFetch = (): Promise<AppBootstrapStatus> => {
  let resolveRef: (appBoostrapStatus: AppBootstrapStatus) => void;
  let rejectRef: (appBoostrapStatusError: AppBootstrapStatus) => void;
  const sourceFetch = window.fetch;

  window.fetch = function (...parameters) {
    return new Promise((resolve, reject) => {
      sourceFetch
        .apply(this, parameters)
        .then((response) => {
          if (response.ok) {
            resolve(response);
            initUser(() => resolveRef(AppBootstrapStatus.SUCCESS));
          } else {
            reject(response);
            rejectRef(AppBootstrapStatus.USER_AUTHENTICATION_ERROR);
          }
        })
        .catch((error) => {
          console.error(error);
          reject(error);

          rejectRef(AppBootstrapStatus.USER_AUTHENTICATION_ERROR);
        })
        .finally(() => {
          window.fetch = sourceFetch;
        });
    });
  };

  return new Promise((resolve, reject) => {
    resolveRef = resolve;
    rejectRef = reject;
  });
};

const handleError = (error: unknown) => {
  if (error instanceof AmplifyInitializationError) {
    return Promise.reject(
      new Error(AppBootstrapStatus.AMPLIFY_INITIALIZATION_ERROR, {
        cause: error,
      }),
    );
  }
  return Promise.reject(
    new Error(AppBootstrapStatus.USER_AUTHENTICATION_ERROR, { cause: error }),
  );
};

const isAuthenticationInProgress = window.location.href.match(/code=/);

const handleAuthentication = async (
  promise: Promise<AppBootstrapStatus>,
): Promise<AppBootstrapStatus> => {
  let newPromise: Promise<AppBootstrapStatus> = promise;

  // redirect returned from Cognito
  if (isAuthenticationInProgress) {
    newPromise = overrideFetch();
  } else {
    localStorage.setItem(
      "LOGIN_REDIRECT_PATH",
      `${window.location.pathname}${window.location.search}`,
    );
  }

  await configure();
  const user = await getCurrentUser();

  // initing User Authentication
  if (!isAuthenticationInProgress && !user) {
    await signInWithRedirect();

    newPromise = Promise.resolve(AppBootstrapStatus.USER_AUTHENTICATION);
  } else if (user?.identityToken) {
    setAxiosAuthorizationHeader(`Bearer ${user.identityToken}`);
  }
  return newPromise;
};

const AmplifyBootstrap = async (): Promise<AppBootstrapStatus> => {
  let promise = Promise.resolve(AppBootstrapStatus.SUCCESS);

  try {
    await initializeAxiosInstances();
    promise = Promise.resolve(await handleAuthentication(promise));
  } catch (error) {
    promise = handleError(error);
  }

  return promise;
};

export { AmplifyBootstrap };
